// One user: /users/:id
//
// Name saves on its own. Deactivate / reactivate and grant / revoke
// super-admin go through a confirm dialog. The UI mirrors the server's
// rules (and still shows its errors if they differ):
//   • you can't change your own super-admin role or active state (403)
//   • the last ACTIVE super-admin can't be demoted or deactivated (409)

import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import type { PlatformUser, UserPatch } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  EditableTextField,
  EmptyState,
  ErrorState,
  KeyValueList,
  LoadingState,
  RelativeTime,
  useToast,
} from '@/components/ui'
import { ChevronRightIcon } from '@/components/ui/icons'
import { formatDate } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { UserRoleBadges, UserStatusBadge } from './UserBadges'

type Action = 'deactivate' | 'reactivate' | 'promote' | 'demote'

const ROLE_LABEL = { admin: 'Admin', editor: 'Editor', 'stat-keeper': 'Stat-keeper' } as const

function ActionRow({ title, body, action, note }: { title: string; body: string; action: ReactNode; note?: string | null }) {
  return (
    <div className="flex flex-col gap-3 border-t border-line py-4 first:border-t-0 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-[13px] text-muted">{body}</p>
        {note ? <p className="mt-1.5 text-xs text-warn">{note}</p> : null}
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}

export default function UserDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const { user: me } = useAuth()
  const toast = useToast()
  const [dialog, setDialog] = useState<Action | null>(null)

  const { data: user, error, status, refetch } = useQuery(validId ? ['users', id] : null, (signal) => api.users.get(id, signal))
  const supers = useQuery(validId ? ['users', 'supers'] : null, (signal) => api.users.list({ kind: 'super_admin', page_size: 100 }, signal))
  useDocumentTitle(user ? user.name || user.email : 'User')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState
          title="User not found"
          description={validId ? `There's no user with id ${id}.` : `“${rawId}” isn't a user id.`}
          action={<ButtonLink to="/users">Back to users</ButtonLink>}
        />
      </Card>
    )
  }
  if (status === 'loading' && !user) return <LoadingState label="Loading user…" />
  if (!user) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this user" onRetry={refetch} />
      </Card>
    )
  }

  const isSelf = me?.id === user.id
  const isSuper = user.platform_role === 'super_admin'
  const activeSupers = (supers.data?.users ?? []).filter((u) => u.platform_role === 'super_admin' && u.is_active).length
  const isLastSuper = isSuper && user.is_active && !!supers.data && activeSupers <= 1
  const selfNote = isSelf ? "You can't change your own super-admin role or deactivate yourself. Ask another super-admin." : null
  const lastSuperNote = isLastSuper ? `${user.name || user.email} is the only active super-admin. Make someone else a super-admin first.` : null

  const apply = (u: PlatformUser) => {
    setQueryData(['users', u.id], u)
    invalidateQueries('users')
  }
  const save = async (patch: UserPatch) => apply(await api.users.update(user.id, patch))
  const who = user.name || user.email

  const DIALOGS: Record<Action, { title: string; description: string; confirm: string; tone: 'danger' | 'primary'; patch: UserPatch; done: string }> = {
    deactivate: {
      title: `Deactivate ${who}?`,
      description: 'They can no longer sign in anywhere: this console or any league admin. Their memberships stay, so reactivating restores access.',
      confirm: 'Deactivate',
      tone: 'danger',
      patch: { is_active: false },
      done: `${who} is deactivated.`,
    },
    reactivate: {
      title: `Reactivate ${who}?`,
      description: 'They can sign in again with their existing password, with the same memberships.',
      confirm: 'Reactivate',
      tone: 'primary',
      patch: { is_active: true },
      done: `${who} can sign in again.`,
    },
    promote: {
      title: `Make ${who} a super-admin?`,
      description: 'Super-admins can see and change every tenant, every user and billing, and can sign in to this console.',
      confirm: 'Make super-admin',
      tone: 'primary',
      patch: { platform_role: 'super_admin' },
      done: `${who} is now a super-admin.`,
    },
    demote: {
      title: `Remove ${who}'s super-admin role?`,
      description: 'They lose access to this console straight away. League memberships are untouched.',
      confirm: 'Remove super-admin',
      tone: 'danger',
      patch: { platform_role: null },
      done: `${who} is no longer a super-admin.`,
    },
  }
  const d = dialog ? DIALOGS[dialog] : null

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Users', to: '/users' }, { label: who }]}
        title={who}
        subtitle={user.name ? <span className="break-all">{user.email}</span> : undefined}
        badges={
          <>
            {isSelf ? <Badge tone="outline">You</Badge> : null}
            <UserRoleBadges user={user} />
            <UserStatusBadge active={user.is_active} />
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Profile" />
            <div className="grid gap-5">
              <EditableTextField label="Name" value={user.name ?? ''} placeholder="No name" maxLength={120} onSave={(name) => save({ name: name || null })} />
              <KeyValueList
                items={[
                  { label: 'Email', value: <span className="break-all font-mono text-xs">{user.email}</span> },
                  { label: 'Last login', value: <RelativeTime value={user.last_login_at} /> },
                  { label: 'Created', value: formatDate(user.created_at) },
                  { label: 'User id', value: <span className="font-mono">{user.id}</span> },
                ]}
              />
            </div>
          </Card>

          <Card flush className="overflow-hidden">
            <div className="px-4 pt-4 sm:px-5">
              <CardHeader title="League memberships" description="Roles are changed on each tenant's People tab." />
            </div>
            {user.memberships.length === 0 ? (
              <EmptyState
                className="py-8"
                title="No league"
                description={isSuper ? 'Super-admins reach every tenant without a membership.' : 'This account can’t reach any league admin. Add it from a tenant’s People tab, or deactivate it.'}
              />
            ) : (
              <ul aria-label="League memberships" className="divide-y divide-line border-t border-line">
                {user.memberships.map((m) => (
                  <li key={m.league_id}>
                    <Link to={`/tenants/${m.league_id}/people`} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-hover/50 sm:px-5">
                      <span className="grid min-w-0 flex-1 gap-0.5">
                        <span className="truncate font-semibold text-ink">{m.league_name}</span>
                        {m.subdomain ? <span className="truncate font-mono text-xs text-muted">{m.subdomain}</span> : null}
                      </span>
                      <Badge tone="neutral">{ROLE_LABEL[m.role] ?? m.role}</Badge>
                      <ChevronRightIcon size={16} className="shrink-0 text-subtle" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card>
          <CardHeader title="Access" description="Every change here is logged in Audit." />
          <ActionRow
            title={isSuper ? 'Super-admin' : 'Not a super-admin'}
            body={isSuper ? 'Can use this console and change any tenant.' : 'Can only reach the leagues they belong to.'}
            note={selfNote ?? (isSuper ? lastSuperNote : null)}
            action={
              <Button variant={isSuper ? 'secondary' : 'primary'} disabled={isSelf || (isSuper && isLastSuper)} onClick={() => setDialog(isSuper ? 'demote' : 'promote')}>
                {isSuper ? 'Remove super-admin…' : 'Make super-admin…'}
              </Button>
            }
          />
          <ActionRow
            title={user.is_active ? 'Active' : 'Deactivated'}
            body={user.is_active ? 'Can sign in.' : 'Can’t sign in anywhere until reactivated.'}
            note={user.is_active ? selfNote ?? lastSuperNote : null}
            action={
              user.is_active ? (
                <Button variant="danger" disabled={isSelf || isLastSuper} onClick={() => setDialog('deactivate')}>
                  Deactivate…
                </Button>
              ) : (
                <Button onClick={() => setDialog('reactivate')}>Reactivate…</Button>
              )
            }
          />
        </Card>
      </div>

      {d ? (
        <ConfirmDialog
          open
          onClose={() => setDialog(null)}
          title={d.title}
          description={d.description}
          confirmLabel={d.confirm}
          tone={d.tone}
          onConfirm={async () => {
            await save(d.patch)
            toast.success(d.done)
          }}
        />
      ) : null}
    </div>
  )
}
